import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  Collapse,
  Paper,
  Chip,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import PermissionService from '../../../Services/permission.service';
import { MENU_ITEM_KEY_PREFIX, MENU_CATEGORY_KEY_PREFIX } from '../../../assests/Constants/menuRestrictionTranslationKeys.js';

export default function SubAccountantCaseRestrictionsEditor({
  subAccountantId,
  token,
  restrictionScope,
  setRestrictionScope,
  selectedClientsCount = 0,
  restrictionKeys,
  onRestrictionKeysChange,
  t,
  loadRestrictionsFromParent = false,
}) {
  const [loadingRestrictions, setLoadingRestrictions] = useState(false);
  const [keyDefinitions, setKeyDefinitions] = useState([]);
  const [definitionsLoading, setDefinitionsLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState({});

  const caseIdForApi = null;

  // Load key definitions from DB (categories + items)
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setDefinitionsLoading(true);
    PermissionService.GetRestrictionKeyDefinitions(token)
      .then((list) => {
        if (!cancelled) setKeyDefinitions(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setKeyDefinitions([]);
      })
      .finally(() => {
        if (!cancelled) setDefinitionsLoading(false);
      });
    return () => { cancelled = true; };
  }, [token]);

  // Load current restrictions when scope or case changes (skip when parent supplies from grouped data to avoid showing global+client merged)
  useEffect(() => {
    if (loadRestrictionsFromParent || !subAccountantId || !token) return;
    let cancelled = false;
    setLoadingRestrictions(true);
    PermissionService.GetRestrictionsBySubAndCase(subAccountantId, caseIdForApi, token)
      .then((keys) => {
        if (!cancelled) onRestrictionKeysChange(Array.isArray(keys) ? keys : []);
      })
      .catch(() => {
        if (!cancelled) onRestrictionKeysChange([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingRestrictions(false);
      });
    return () => { cancelled = true; };
  }, [loadRestrictionsFromParent, subAccountantId, caseIdForApi, token]);

  const { categories, itemsByCategory } = useMemo(() => {
    const list = keyDefinitions || [];
    const cats = list
      .filter((d) => !d.categoryKey)
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .map((d) => ({ key: d.key, displayName: d.displayName || d.key }));
    const byCat = {};
    list
      .filter((d) => d.categoryKey)
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .forEach((d) => {
        const cat = d.categoryKey;
        if (!byCat[cat]) byCat[cat] = [];
        byCat[cat].push({ key: d.key, displayName: d.displayName || d.key });
      });
    return { categories: cats, itemsByCategory: byCat };
  }, [keyDefinitions]);

  useEffect(() => {
    if (categories.length === 0) return;
    setExpandedCategories((prev) => {
      const next = {};
      categories.forEach((c) => {
        next[c.key] = prev[c.key] !== false;
      });
      return Object.keys(next).length ? next : prev;
    });
  }, [keyDefinitions.length]);

  const toggleCategory = (categoryKey) => {
    const set = new Set(restrictionKeys);
    if (set.has(categoryKey)) set.delete(categoryKey);
    else set.add(categoryKey);
    onRestrictionKeysChange([...set]);
  };

  const toggleItem = (itemKey) => {
    const set = new Set(restrictionKeys);
    if (set.has(itemKey)) set.delete(itemKey);
    else set.add(itemKey);
    onRestrictionKeysChange([...set]);
  };

  const toggleCategoryExpand = (categoryKey) => {
    setExpandedCategories((prev) => ({ ...prev, [categoryKey]: !prev[categoryKey] }));
  };

  const restrictionSet = useMemo(() => new Set(restrictionKeys || []), [restrictionKeys]);

  const getCategoryLabel = (key, displayName) =>
    t(`menuCategory.${key}`) || displayName || key;
  const getItemLabel = (key, displayName) =>
    t(`menuItem.${key}`) || displayName || key;

  const getRestrictionTagLabel = (key) => {
    const itemLabel = t(`${MENU_ITEM_KEY_PREFIX}.${key}`);
    if (itemLabel !== `${MENU_ITEM_KEY_PREFIX}.${key}`) return itemLabel;
    const catLabel = t(`${MENU_CATEGORY_KEY_PREFIX}.${key}`);
    if (catLabel !== `${MENU_CATEGORY_KEY_PREFIX}.${key}`) return catLabel;
    return key;
  };

  const assignedKeys = useMemo(() => Array.isArray(restrictionKeys) ? restrictionKeys : [], [restrictionKeys]);

  return (
    <Box sx={{ mt: 0, mb: 0 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
        {t('SubAccountantCaseRestrictions') || 'Per-client menu restrictions'}
      </Typography>

      {/* Assigned restrictions as tags */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 600 }}>
          {t('AssignedRestrictions') || 'Assigned restrictions'}
        </Typography>
        {assignedKeys.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {t('NoRestrictions') || 'No restrictions'}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {assignedKeys.map((key) => (
              <Chip
                key={key}
                label={getRestrictionTagLabel(key)}
                size="small"
                icon={<LockIcon sx={{ fontSize: 14 }} />}
                variant="outlined"
                sx={{ fontSize: '12px', height: 26 }}
              />
            ))}
          </Box>
        )}
      </Box>

      <RadioGroup
        row
        value={restrictionScope}
        onChange={(e) => {
          setRestrictionScope(e.target.value);
        }}
        sx={{ mb: 1.5, flexWrap: 'wrap' }}
      >
        <FormControlLabel
          value="all"
          control={<Radio />}
          label={t('RestrictionsScopeAllClientsGlobal') || 'All clients (global)'}
        />
        <FormControlLabel
          value="clients"
          control={<Radio />}
          label={t('RestrictionsScopeSelectedClients') || 'Selected clients'}
        />
      </RadioGroup>

      {restrictionScope === 'clients' && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          {selectedClientsCount === 0
            ? (t('RestrictionsSelectClientsHint') || 'Use the list above to select one or more clients.')
            : (t('RestrictionsMultiEditHint', { count: selectedClientsCount }) ||
              `Editing restrictions for ${selectedClientsCount} client(s). Save applies the same menu locks to each.`)}
        </Typography>
      )}

      {definitionsLoading ? (
        <Typography variant="body2" color="text.secondary">
          {t('Loading') || 'Loading...'}
        </Typography>
      ) : loadingRestrictions ? (
        <Typography variant="body2" color="text.secondary">
          {t('Loading') || 'Loading...'}
        </Typography>
      ) : categories.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('NoRestrictions') || 'No restriction keys defined.'}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {categories.map((cat) => {
            const categoryKey = cat.key;
            const items = itemsByCategory[categoryKey] || [];
            const categoryDenied = restrictionSet.has(categoryKey);
            const expanded = expandedCategories[categoryKey] !== false;
            return (
              <Paper
                key={categoryKey}
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  backgroundColor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={categoryDenied}
                      onChange={() => toggleCategory(categoryKey)}
                      color="primary"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {getCategoryLabel(categoryKey, cat.displayName)}
                      </Typography>
                      {items.length > 0 && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          onClick={(e) => { e.preventDefault(); toggleCategoryExpand(categoryKey); }}
                          sx={{ cursor: 'pointer', ml: 0.5 }}
                        >
                          ({expanded ? t('Hide') || 'Hide' : t('Show') || 'Show'} {items.length})
                        </Typography>
                      )}
                    </Box>
                  }
                />
                <Collapse in={expanded}>
                  <Box
                    sx={{
                      pl: 3,
                      pt: 0.5,
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                      gap: 0.5,
                    }}
                  >
                    {items.map((item) => (
                      <FormControlLabel
                        key={item.key}
                        control={
                          <Checkbox
                            checked={restrictionSet.has(item.key)}
                            onChange={() => toggleItem(item.key)}
                            color="primary"
                            size="small"
                          />
                        }
                        label={<Typography variant="body2">{getItemLabel(item.key, item.displayName)}</Typography>}
                        sx={{ m: 0, alignItems: 'flex-start' }}
                      />
                    ))}
                  </Box>
                </Collapse>
              </Paper>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
